import { Link } from 'react-router-dom';

function DataRetention() {
  const lastUpdated = "December 2025";
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="text-blue-200 hover:text-white text-sm mb-4 inline-block">
            ← Back to Home
          </Link>
          <h1 className="text-4xl font-bold mb-2">Data Retention & Disposal Policy</h1>
          <p className="text-blue-100">Effective Date: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
          
          {/* Purpose */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Purpose</h2>
            <p className="text-gray-700 leading-relaxed">
              This policy defines how GuapGoals collects, retains, and disposes of user data to ensure 
              compliance with privacy regulations and security best practices.
            </p>
          </section>

          {/* Data Categories */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Data Categories and Retention Periods</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Data Category</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Retention Period</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">Disposal Method</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="border border-gray-200 px-4 py-3"><strong>Account Information</strong><br/>(name, email)</td>
                    <td className="border border-gray-200 px-4 py-3">Duration of active account + 30 days after deletion request</td>
                    <td className="border border-gray-200 px-4 py-3">Permanent deletion from database</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3"><strong>Authentication Credentials</strong></td>
                    <td className="border border-gray-200 px-4 py-3">Duration of active account</td>
                    <td className="border border-gray-200 px-4 py-3">Secure deletion; passwords are hashed (never stored in plain text)</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-3"><strong>Session Tokens</strong></td>
                    <td className="border border-gray-200 px-4 py-3">24 hours or until logout</td>
                    <td className="border border-gray-200 px-4 py-3">Automatic expiration and database purge</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3"><strong>Financial Data</strong><br/>(bills, income, budgets)</td>
                    <td className="border border-gray-200 px-4 py-3">Duration of active account + 30 days after deletion request</td>
                    <td className="border border-gray-200 px-4 py-3">Permanent deletion from database</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-3"><strong>Plaid Access Tokens</strong></td>
                    <td className="border border-gray-200 px-4 py-3">Until user unlinks bank OR account deletion</td>
                    <td className="border border-gray-200 px-4 py-3">Secure deletion; Plaid item revoked via API</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3"><strong>Transaction Data</strong><br/>(from Plaid)</td>
                    <td className="border border-gray-200 px-4 py-3">Only processed in-memory for bill detection; recurring bill summaries stored with user consent</td>
                    <td className="border border-gray-200 px-4 py-3">Permanent deletion upon account deletion</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-3"><strong>2FA Secrets</strong></td>
                    <td className="border border-gray-200 px-4 py-3">Duration of active account or until 2FA disabled</td>
                    <td className="border border-gray-200 px-4 py-3">Secure deletion from database</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-3"><strong>Audit/Activity Logs</strong></td>
                    <td className="border border-gray-200 px-4 py-3">90 days</td>
                    <td className="border border-gray-200 px-4 py-3">Automatic purge after retention period</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-3"><strong>Support Communications</strong></td>
                    <td className="border border-gray-200 px-4 py-3">1 year after resolution</td>
                    <td className="border border-gray-200 px-4 py-3">Secure deletion</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Disposal Procedures */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Data Disposal Procedures</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.1 User-Initiated Deletion</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Users may request account deletion via in-app settings or by contacting privacy@guapgoals.com</li>
              <li>Deletion completed within 30 days of verified request</li>
              <li>Confirmation sent to user upon completion</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.2 Bank Account Unlinking</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Users can unlink bank accounts at any time via the Bills page</li>
              <li>Plaid access tokens are immediately deleted from our database</li>
              <li>Plaid item is revoked via Plaid API to terminate access</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.3 Automatic Disposal</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Expired session tokens purged automatically</li>
              <li>Audit logs automatically deleted after 90-day retention period</li>
              <li>Inactive accounts (no login for 24 months) flagged for review and potential deletion with notice</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">3.4 Secure Deletion Methods</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Database records permanently deleted (not soft-deleted)</li>
              <li>No backup retention of deleted user data beyond 30 days</li>
              <li>Deletion verified through database confirmation</li>
            </ul>
          </section>

          {/* Data We Never Store */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data We Never Store</h2>
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Bank login credentials (handled exclusively by Plaid)
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Full account numbers
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Social Security numbers
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-600">✓</span>
                  Raw transaction history (only recurring bill summaries)
                </li>
              </ul>
            </div>
          </section>

          {/* Third-Party */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Data Handling</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-3">Plaid Inc.</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Plaid retains data per their own retention policy</li>
              <li>Users can revoke Plaid access via <a href="https://my.plaid.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">my.plaid.com</a></li>
              <li>GuapGoals requests only necessary transaction data (recurring bills)</li>
            </ul>
            <p className="text-gray-700 mt-4">
              For more information, see{' '}
              <a 
                href="https://plaid.com/legal/#end-user-privacy-policy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 underline"
              >
                Plaid's End User Privacy Policy
              </a>.
            </p>
          </section>

          {/* Policy Review */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Policy Review</h2>
            <p className="text-gray-700 leading-relaxed">
              This policy is reviewed annually and updated as needed to reflect changes in regulations 
              or business practices. Users will be notified of material changes via email or in-app notification.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Contact</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For data retention inquiries or deletion requests:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700"><strong>Email:</strong> <a href="mailto:privacy@guapgoals.com" className="text-blue-600 hover:text-blue-700">privacy@guapgoals.com</a></p>
              <p className="text-gray-700 mt-2"><strong>In-App:</strong> Settings → Security → Delete Account</p>
            </div>
          </section>

        </div>

        {/* Footer Links */}
        <div className="mt-8 flex justify-center gap-6">
          <Link to="/privacy" className="text-blue-600 hover:text-blue-700 font-medium">
            Privacy Policy
          </Link>
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to GuapGoals
          </Link>
        </div>
      </div>
    </div>
  );
}

export default DataRetention;
